import { useCallback, useEffect, useMemo, useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"

import { COST_BREAKDOWN_DECIMAL_PRECISION } from "../constants"
import type { AmountPercentagePair } from "../types"
import {
  calculateAllocatedGrCostAmount,
  calculateAllocatedGrCostPerUom,
  calculateAllocatedGrCostPercentage,
  calculateBareCostPerUomFromTotalBareCost,
  calculateFreightAndMiscCostPerUom,
  calculateQuantityInUom,
  calculateTotalBareCost,
  calculateTotalLaidDownCostPerUom,
  convertMetricPriceToUs,
  convertUsPriceToMetric,
  getCostBreakdownProductFieldPaths,
} from "../utils"

import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import type { PurchaseOrderLineWithReceiptContext } from "@/screens/WarehouseReceiving/types"
import { runWithGuardRef } from "@/utils/forms"
import { roundToPrecision } from "@/utils/number"

// AK-6569: Rounds raw calculated values when writing to the form or returning to the caller,
// so chaining the calculations doesn't accumulate error.
const roundValue = (value: number | null): number | null =>
  value && roundToPrecision(value, COST_BREAKDOWN_DECIMAL_PRECISION)

interface UseProductCalculationsConsolidatedProps {
  productIndex: number
  purchaseOrderLine: PurchaseOrderLineWithReceiptContext
  goodsReceipt: GoodsReceipt
}

interface ProductCalculations {
  totalBareCost: number | null
  allocatedGrCostPercentage: number | null
  allocatedGrCostAmount: number | null
  allocatedGrCostPerUom: number | null
  totalLaidDownCostPerUom: number | null
  freightAndMiscCostPerUom: number | null
  quantityInUom: number
}

export const useProductCalculations = ({
  productIndex,
  purchaseOrderLine,
  goodsReceipt,
}: UseProductCalculationsConsolidatedProps): ProductCalculations => {
  const { setValue, getValues } = useFormContext()

  const fieldPaths = useMemo(() => getCostBreakdownProductFieldPaths(productIndex), [productIndex])

  // Helper function to clear standard amount-percentage pairs
  const clearStandardAmountPercentagePairs = useCallback(() => {
    setValue(fieldPaths.duties.amount, null)
    setValue(fieldPaths.duties.percentage, null)
    setValue(fieldPaths.mpf.amount, null)
    setValue(fieldPaths.mpf.percentage, null)
    setValue(fieldPaths.hmf.amount, null)
    setValue(fieldPaths.hmf.percentage, null)
  }, [setValue, fieldPaths])

  // Refs to prevent infinite loops (from original implementation)
  const lastCalculatedTotalBareCost = useRef<number | null>(null)
  const isUpdatingFromBidirectional = useRef<boolean>(false)

  // Refs for price conversions
  const lastCalculatedPricePerMetricUom = useRef<number | null>(null)
  const isUpdatingPriceConversion = useRef<boolean>(false)

  // Refs for bare cost conversions
  const lastCalculatedBareCostPerMetricUom = useRef<number | null>(null)
  const isUpdatingBareCostConversion = useRef<boolean>(false)

  const bareCostPerUom = useWatch({
    control: undefined,
    name: fieldPaths.bareCostPerUom,
  })
  const pricePerUom = useWatch({
    control: undefined,
    name: fieldPaths.pricePerUom,
  })
  const pricePerMetricUom = useWatch({
    control: undefined,
    name: fieldPaths.pricePerMetricUom,
  })
  const bareCostPerMetricUom = useWatch({
    control: undefined,
    name: fieldPaths.bareCostPerMetricUom,
  })
  const insurance = useWatch({
    control: undefined,
    name: fieldPaths.insurance,
  })
  const sharedGrCost = useWatch({
    control: undefined,
    name: "sharedGrCost",
  })
  const tariffs = useWatch({
    control: undefined,
    name: fieldPaths.tariffs,
  })
  const atypicalSurcharges = useWatch({
    control: undefined,
    name: fieldPaths.atypicalSurcharges,
  })
  const dutiesAmount = useWatch({
    control: undefined,
    name: fieldPaths.duties.amount,
  })
  const mpfAmount = useWatch({
    control: undefined,
    name: fieldPaths.mpf.amount,
  })
  const hmfAmount = useWatch({
    control: undefined,
    name: fieldPaths.hmf.amount,
  })
  const totalBareCostField = useWatch({
    control: undefined,
    name: fieldPaths.totalBareCost,
  })

  // Calculate quantity in UoM by summing ALL pallet rows for this order line
  const quantityInUom = useMemo(() => {
    return calculateQuantityInUom(purchaseOrderLine)
  }, [purchaseOrderLine])

  const totalBareCost = useMemo(() => {
    return calculateTotalBareCost(bareCostPerUom, quantityInUom)
  }, [bareCostPerUom, quantityInUom])

  // Use totalBareCostField for percentage calculations (preserves user input precision)
  // Use calculated totalBareCost for form updates (enables forward calculation)
  const totalBareCostForPercentages = totalBareCostField ?? totalBareCost

  // Calculate allocated GR cost percentage: quantityInUom / (sum of all quantityInUom) * 100
  const allocatedGrCostPercentage = useMemo(() => {
    return calculateAllocatedGrCostPercentage(quantityInUom, goodsReceipt)
  }, [quantityInUom, goodsReceipt])

  // Calculate allocated GR cost amount: allocatedGrCostPercentage * sharedGrCost / 100
  const allocatedGrCostAmount = useMemo(() => {
    return calculateAllocatedGrCostAmount(allocatedGrCostPercentage, sharedGrCost)
  }, [allocatedGrCostPercentage, sharedGrCost])

  // Calculate allocated GR cost per UoM: allocatedGrCostAmount / quantityInUom
  const allocatedGrCostPerUom = useMemo(() => {
    return calculateAllocatedGrCostPerUom(allocatedGrCostAmount, quantityInUom)
  }, [allocatedGrCostAmount, quantityInUom])

  // Calculate total laid-down cost per UoM
  const totalLaidDownCostPerUom = useMemo(() => {
    return calculateTotalLaidDownCostPerUom({
      quantityInUom,
      bareCostPerUom,
      insurance,
      allocatedGrCostAmount,
      dutiesAmount,
      mpfAmount,
      hmfAmount,
      tariffs: tariffs as AmountPercentagePair[],
      atypicalSurcharges: atypicalSurcharges as { type?: any; description?: string; amount?: number }[],
    })
  }, [
    quantityInUom,
    bareCostPerUom,
    insurance,
    allocatedGrCostAmount,
    dutiesAmount,
    mpfAmount,
    hmfAmount,
    tariffs,
    atypicalSurcharges,
  ])

  const freightAndMiscCostPerUom = useMemo(() => {
    return calculateFreightAndMiscCostPerUom({
      quantityInUom,
      insurance,
      allocatedGrCostAmount,
      dutiesAmount,
      mpfAmount,
      hmfAmount,
      tariffs: tariffs as AmountPercentagePair[],
      atypicalSurcharges: atypicalSurcharges as { type?: any; description?: string; amount?: number }[],
    })
  }, [quantityInUom, insurance, allocatedGrCostAmount, dutiesAmount, mpfAmount, hmfAmount, tariffs, atypicalSurcharges])

  useEffect(() => {
    setValue(fieldPaths.allocatedGrCostPercentage, roundValue(allocatedGrCostPercentage))
  }, [allocatedGrCostPercentage, setValue, fieldPaths])

  useEffect(() => {
    setValue(fieldPaths.allocatedGrCostAmount, roundValue(allocatedGrCostAmount) ?? 0)
  }, [allocatedGrCostAmount, setValue, fieldPaths])

  useEffect(() => {
    setValue(fieldPaths.allocatedGrCostPerUom, roundValue(allocatedGrCostPerUom) ?? 0)
  }, [allocatedGrCostPerUom, setValue, fieldPaths])

  useEffect(() => {
    setValue(fieldPaths.totalLaidDownCostPerUom, roundValue(totalLaidDownCostPerUom) ?? 0)
  }, [totalLaidDownCostPerUom, setValue, fieldPaths])

  useEffect(() => {
    setValue(fieldPaths.freightAndMiscCostPerUom, roundValue(freightAndMiscCostPerUom) ?? 0)
  }, [freightAndMiscCostPerUom, setValue, fieldPaths])

  useEffect(() => {
    if (bareCostPerUom === null || bareCostPerUom === undefined) {
      setValue(fieldPaths.bareCostPerUom, 0)
    }
  }, [bareCostPerUom, setValue, fieldPaths])

  useEffect(() => {
    if (totalBareCostField === null || totalBareCostField === undefined) {
      setValue(fieldPaths.totalBareCost, 0)
    }
  }, [totalBareCostField, setValue, fieldPaths])

  useEffect(() => {
    if (totalBareCostField === 0) {
      clearStandardAmountPercentagePairs()

      const currentTariffs = tariffs || []
      currentTariffs.forEach((_: any, tariffIndex: number) => {
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.amount`, null)
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.percentage`, null)
      })
    }
  }, [totalBareCostField, clearStandardAmountPercentagePairs, setValue, fieldPaths, tariffs])

  // Clear all amount-percentage pairs when bareCostPerUom is cleared
  useEffect(() => {
    const wasCleared = bareCostPerUom === null || bareCostPerUom === undefined

    if (wasCleared) {
      setValue(fieldPaths.totalBareCost, null)
      clearStandardAmountPercentagePairs()

      const currentValues = getValues()
      const currentTariffs = currentValues.products?.[productIndex]?.tariffs || []
      currentTariffs.forEach((_: AmountPercentagePair, tariffIndex: number) => {
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.amount`, null)
        setValue(`${fieldPaths.tariffs}.${tariffIndex}.percentage`, null)
      })
    }
  }, [bareCostPerUom, clearStandardAmountPercentagePairs, setValue, getValues, fieldPaths, productIndex])

  useEffect(() => {
    if (pricePerUom === null || pricePerUom === undefined) {
      setValue(fieldPaths.isCompleted, false)
    }
  }, [pricePerUom, setValue, fieldPaths])

  // Forward calculation: bareCostPerUom changes -> update totalBareCost
  useEffect(() => {
    if (!quantityInUom || quantityInUom === 0 || isUpdatingFromBidirectional.current) return

    // Only update if the calculated value changed (not when user is typing)
    if (totalBareCost !== null && totalBareCost !== lastCalculatedTotalBareCost.current) {
      runWithGuardRef(() => {
        setValue(fieldPaths.totalBareCost, totalBareCost)
        // Update lastCalculatedTotalBareCost to track what we just set
        lastCalculatedTotalBareCost.current = totalBareCost
      }, isUpdatingFromBidirectional)
    }
  }, [totalBareCost, quantityInUom, setValue, fieldPaths])

  // Backward calculation: totalBareCost changes -> update bareCostPerUom
  useEffect(() => {
    if (!quantityInUom || quantityInUom === 0) return

    const currentTotalBareCostField = totalBareCostField

    // Only trigger bidirectional calculation if:
    // 1. The field value is different from what we last calculated
    // 2. We're not already in a bidirectional update
    // 3. The value is not null/undefined
    if (
      currentTotalBareCostField !== lastCalculatedTotalBareCost.current &&
      !isUpdatingFromBidirectional.current &&
      currentTotalBareCostField !== null &&
      currentTotalBareCostField !== undefined
    ) {
      runWithGuardRef(() => {
        // Calculate bareCostPerUom from totalBareCost
        const newBareCostPerUom = calculateBareCostPerUomFromTotalBareCost(currentTotalBareCostField, quantityInUom)
        setValue(fieldPaths.bareCostPerUom, newBareCostPerUom)

        // CRITICAL: Update lastCalculatedTotalBareCost to the USER'S INPUT VALUE
        // This prevents the forward calculation from overwriting the user's exact input
        lastCalculatedTotalBareCost.current = currentTotalBareCostField
      }, isUpdatingFromBidirectional)
    }
  }, [totalBareCostField, quantityInUom, setValue, fieldPaths])

  // Forward calculation: pricePerUom changes -> update pricePerMetricUom
  useEffect(() => {
    if (isUpdatingPriceConversion.current || pricePerUom === null || pricePerUom === undefined) return

    const convertedPrice = convertUsPriceToMetric(pricePerUom, purchaseOrderLine.product, purchaseOrderLine.priceUomId)

    if (convertedPrice !== null && convertedPrice !== lastCalculatedPricePerMetricUom.current) {
      runWithGuardRef(() => {
        setValue(fieldPaths.pricePerMetricUom, convertedPrice)
        lastCalculatedPricePerMetricUom.current = convertedPrice
      }, isUpdatingPriceConversion)
    }
  }, [pricePerUom, purchaseOrderLine, setValue, fieldPaths])

  // Backward calculation: pricePerMetricUom changes -> update pricePerUom
  useEffect(() => {
    if (
      isUpdatingPriceConversion.current ||
      pricePerMetricUom === lastCalculatedPricePerMetricUom.current ||
      pricePerMetricUom === null ||
      pricePerMetricUom === undefined
    ) {
      return
    }

    runWithGuardRef(() => {
      const convertedPrice = convertMetricPriceToUs(
        pricePerMetricUom,
        purchaseOrderLine.product,
        purchaseOrderLine.priceUomId
      )

      if (convertedPrice !== null) {
        setValue(fieldPaths.pricePerUom, convertedPrice)
      }

      lastCalculatedPricePerMetricUom.current = pricePerMetricUom
    }, isUpdatingPriceConversion)
  }, [pricePerMetricUom, purchaseOrderLine, setValue, fieldPaths])

  // Forward calculation: bareCostPerUom changes -> update bareCostPerMetricUom
  useEffect(() => {
    if (isUpdatingBareCostConversion.current || bareCostPerUom === null || bareCostPerUom === undefined) return

    const convertedBareCost = convertUsPriceToMetric(
      bareCostPerUom,
      purchaseOrderLine.product,
      purchaseOrderLine.priceUomId
    )

    if (convertedBareCost !== null && convertedBareCost !== lastCalculatedBareCostPerMetricUom.current) {
      runWithGuardRef(() => {
        setValue(fieldPaths.bareCostPerMetricUom, convertedBareCost)
        lastCalculatedBareCostPerMetricUom.current = convertedBareCost
      }, isUpdatingBareCostConversion)
    }
  }, [bareCostPerUom, purchaseOrderLine, setValue, fieldPaths])

  // Backward calculation: bareCostPerMetricUom changes -> update bareCostPerUom
  useEffect(() => {
    if (
      isUpdatingBareCostConversion.current ||
      bareCostPerMetricUom === lastCalculatedBareCostPerMetricUom.current ||
      bareCostPerMetricUom === null ||
      bareCostPerMetricUom === undefined
    ) {
      return
    }

    runWithGuardRef(() => {
      const convertedBareCost = convertMetricPriceToUs(
        bareCostPerMetricUom,
        purchaseOrderLine.product,
        purchaseOrderLine.priceUomId
      )

      if (convertedBareCost !== null) {
        setValue(fieldPaths.bareCostPerUom, convertedBareCost)
      }

      lastCalculatedBareCostPerMetricUom.current = bareCostPerMetricUom
    }, isUpdatingBareCostConversion)
  }, [bareCostPerMetricUom, purchaseOrderLine, setValue, fieldPaths])

  return {
    totalBareCost: totalBareCostForPercentages,
    allocatedGrCostPercentage: roundValue(allocatedGrCostPercentage),
    allocatedGrCostAmount: roundValue(allocatedGrCostAmount),
    allocatedGrCostPerUom: roundValue(allocatedGrCostPerUom),
    totalLaidDownCostPerUom: roundValue(totalLaidDownCostPerUom),
    freightAndMiscCostPerUom: roundValue(freightAndMiscCostPerUom),
    quantityInUom,
  }
}
